import { useCallback, useEffect, useState } from "react";

import { TextArea } from "@fronterahealth/frontera-ui-components";

import { FieldLayout } from "@components/BlurBasedFormFields/InlineFormFields/FieldLayout";
import type { InlineFormFieldProps } from "@components/BlurBasedFormFields/InlineFormFields/types";

export const InlineTextAreaField = <T extends Record<string, unknown>>({
  value,
  fieldKey,
  onSave,
  title,
  className,
  required = false,
}: Omit<Extract<InlineFormFieldProps<T>, { inputType: "textArea" }>, "inputType">) => {
  const normalized = value === "-" || value == null ? "" : value;
  const [inputValue, setInputValue] = useState(normalized);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const next = value === "-" || value == null ? "" : value;
    setInputValue((prev) => (prev !== next ? next : prev));
  }, [value]);

  const handleBlur = useCallback(async () => {
    if (required && inputValue.trim() === "") {
      setError(`${title} is required`);
      return;
    }
    setError(null);
    try {
      const result = await onSave({ [fieldKey]: inputValue } as Partial<T>);
      if (!result.success && result.error) setError(result.error);
    } catch (err) {
      console.error("Error saving field:", err);
      setError("Failed to save changes");
    }
  }, [inputValue, fieldKey, onSave, required, title]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Escape") {
        setInputValue(normalized);
        setError(null);
      }
    },
    [normalized],
  );

  return (
    <FieldLayout title={title} className={className}>
      <TextArea
        label=""
        value={inputValue}
        onChange={(e) => {
          setInputValue(e.target.value);
          if (error) setError(null);
        }}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        rows={3}
        errorMessage={error ?? undefined}
        formNoValidate={!error}
      />
    </FieldLayout>
  );
};
